import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { SOCIALS } from "../../data/velvet";

const LINKS = [
  { label: "The Testament", href: "#manifesto" },
  { label: "Voices", href: "#voices" },
  { label: "Latest Release", href: "#release" },
  { label: "Old Lantern", href: "#label" },
  { label: "Contact", href: "#contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.header
        data-testid="site-nav"
        initial={{ y: -70, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
        className={`fixed top-0 left-0 right-0 z-[100] transition-[background-color,backdrop-filter,border-color] duration-500 ${
          scrolled
            ? "bg-vt-black/80 backdrop-blur-xl border-b border-vt-gold/10"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <nav className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-5 md:px-12">
          <button
            data-testid="nav-logo"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="group text-left"
          >
            <span className="block font-serif text-lg leading-none tracking-tight text-vt-cream md:text-xl">
              The Velvet Testament
            </span>
            <span className="mt-1 block font-mono text-[9px] uppercase tracking-[0.34em] text-vt-gold/70 transition-colors group-hover:text-vt-gold">
              Old Lantern Records
            </span>
          </button>

          <div className="hidden items-center gap-9 lg:flex">
            {LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.href.slice(1)}`}
                onClick={() => go(l.href)}
                className="group relative font-mono text-[11px] uppercase tracking-[0.2em] text-vt-ash transition-colors duration-300 hover:text-vt-cream"
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-vt-gold transition-all duration-500 group-hover:w-full" />
              </button>
            ))}
            <a
              data-testid="nav-spotify"
              href={SOCIALS.spotify}
              target="_blank"
              rel="noreferrer"
              className="rounded-full border border-vt-gold/60 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-vt-gold transition-colors duration-300 hover:bg-vt-gold hover:text-vt-black"
            >
              Listen
            </a>
          </div>

          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen(true)}
            className="text-vt-cream lg:hidden"
            aria-label="Open menu"
          >
            <Menu size={26} strokeWidth={1.2} />
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[110] flex flex-col bg-vt-black/97 backdrop-blur-2xl lg:hidden"
          >
            <div className="flex items-center justify-between px-6 py-5">
              <span className="font-serif text-lg text-vt-cream">The Velvet Testament</span>
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="text-vt-cream" aria-label="Close menu">
                <X size={28} strokeWidth={1.2} />
              </button>
            </div>
            <div className="flex flex-1 flex-col justify-center gap-2 px-8">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  data-testid={`mobile-link-${l.href.slice(1)}`}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.07 }}
                  onClick={() => go(l.href)}
                  className="border-b border-vt-gold/10 py-4 text-left font-serif text-4xl text-vt-cream"
                >
                  {l.label}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
